/**
 * Attendance Permission Constants and Utilities
 * 
 * This module provides a centralized permission system for the Attendance Dashboard.
 * Instead of role-based checks (isHRDirector || isHR || isAdmin || isMaster), 
 * use permission-based checks for better maintainability and extensibility.
 * 
 * Backend should return these abilities in the user's abilities array.
 */

// Permission subjects for attendance module
export const ATTENDANCE_PERMISSIONS = {
  // Dashboard access
  MY_DASHBOARD: 'attendance_my_dashboard',
  TEAM_DASHBOARD: 'attendance_team_dashboard',
  
  // Leave permissions
  APPLY_ON_BEHALF_LEAVE: 'apply_on_behalf_leave',      // Can apply leave for reportees
  APPLY_ON_BEHALF_ALL: 'apply_on_behalf_leave_all',    // Can apply leave for anyone
  APPROVE_LEAVE_MANAGER: 'leave_manager',       // Can approve at manager stage (stage 1)
  APPROVE_LEAVE_HR: 'leave_hr',                 // Can approve at HR stage (stage 2 / final)
  APPROVE_LEAVE_ALL: 'leave_all',               // Can approve at any stage (final approver, org view only)
  
  // Regularization permissions
  APPLY_ON_BEHALF_REGULARIZATION: 'apply_on_behalf_regularization',
  APPLY_ON_BEHALF_REGULARIZATION_ALL: 'apply_on_behalf_regularization_all',
  APPROVE_REGULARIZATION_MANAGER: 'regularization_manager',
  APPROVE_REGULARIZATION_ALL: 'regularization_all',
  
  // Configuration permissions
  MANAGE_CONFIG: 'attendance_config',

  // Announcements / celebrations download (HR panel Download button)
  // Prefer `download` + `announcement` (matches create+announcement). This key is a legacy alias.
  DOWNLOAD_CELEBRATIONS: 'attendance_celebrations',
  ANNOUNCEMENT: 'announcement',
}

// Permission actions
export const PERMISSION_ACTIONS = {
  READ: 'read',
  APPLY: 'apply',
  APPLY_ON_BEHALF: 'apply_on_behalf',
  APPROVE: 'approve',
  MANAGE: 'manage',
}

/**
 * Default abilities mapping for each role type
 * Backend should use this as reference to generate abilities
 */
export const ROLE_ABILITIES_MAP = {
  // All employees get basic access
  employee: [
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.MY_DASHBOARD },
    { action: 'apply', subject: 'leave' },
    { action: 'apply', subject: 'regularization' },
  ],
  
  // Managers (Account Manager, Program Manager, Project Manager)
  manager: [
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.MY_DASHBOARD },
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD },
    { action: 'apply', subject: 'leave' },
    { action: 'apply', subject: 'regularization' },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_LEAVE },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_MANAGER },
    { action: 'download', subject: 'announcement' },
  ],
  
  // HR - Same as managers (first stage approval for reportees)
  hr: [
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.MY_DASHBOARD },
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD },
    { action: 'apply', subject: 'leave' },
    { action: 'apply', subject: 'regularization' },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_LEAVE },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_MANAGER },
    { action: 'create', subject: 'announcement' },
    { action: 'download', subject: 'announcement' },
  ],
  
  // Super Approvers (HR-Director, Admin, Master)
  superApprover: [
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.MY_DASHBOARD },
    { action: 'read', subject: ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD },
    { action: 'apply', subject: 'leave' },
    { action: 'apply', subject: 'regularization' },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_LEAVE },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_ALL },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION },
    { action: 'apply_on_behalf', subject: ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_REGULARIZATION_ALL },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_ALL },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_MANAGER },
    { action: 'approve', subject: ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_ALL },
    { action: 'manage', subject: ATTENDANCE_PERMISSIONS.MANAGE_CONFIG },
    { action: 'download', subject: 'announcement' },
  ],
}

/**
 * Helper to check if user has permission using $can
 * @param {Function} canFn - The $can function from Vue component
 * @param {string} action - The action to check (read, apply, approve, manage)
 * @param {string} subject - The subject to check
 * @returns {boolean}
 */
export function hasPermission(canFn, action, subject) {
  if (typeof canFn !== 'function') return false
  return canFn(action, subject)
}

/**
 * Check if user can view team dashboard
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canViewTeamDashboard(canFn) {
  return hasPermission(canFn, 'read', ATTENDANCE_PERMISSIONS.TEAM_DASHBOARD)
}

/**
 * Check if user can apply leave on behalf of others (reportees only)
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApplyOnBehalf(canFn) {
  return hasPermission(canFn, 'apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_LEAVE)
}

/**
 * Check if user can apply leave on behalf of anyone (all employees)
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApplyOnBehalfAll(canFn) {
  return hasPermission(canFn, 'apply_on_behalf', ATTENDANCE_PERMISSIONS.APPLY_ON_BEHALF_ALL)
}

/**
 * Check if user can approve leaves at manager stage
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApproveLeaveManagerStage(canFn) {
  return hasPermission(canFn, 'approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER)
}

/**
 * Check if user can approve leaves at HR stage
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApproveLeaveHRStage(canFn) {
  return hasPermission(canFn, 'approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_HR)
}

/**
 * Check if user can approve leaves at all stages (final approver)
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApproveLeaveAllStages(canFn) {
  return hasPermission(canFn, 'approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_ALL)
}

/**
 * Check if user can approve regularization at manager stage
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApproveRegularizationManagerStage(canFn) {
  return hasPermission(canFn, 'approve', ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_MANAGER)
}

/**
 * Check if user can approve regularization at all stages (final approver)
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canApproveRegularizationAllStages(canFn) {
  return hasPermission(canFn, 'approve', ATTENDANCE_PERMISSIONS.APPROVE_REGULARIZATION_ALL)
}

/**
 * Check if user can manage attendance configuration
 * @param {Function} canFn - The $can function from Vue component
 * @returns {boolean}
 */
export function canManageConfig(canFn) {
  return hasPermission(canFn, 'manage', ATTENDANCE_PERMISSIONS.MANAGE_CONFIG)
}

export default {
  ATTENDANCE_PERMISSIONS,
  PERMISSION_ACTIONS,
  ROLE_ABILITIES_MAP,
  hasPermission,
  canViewTeamDashboard,
  canApplyOnBehalf,
  canApplyOnBehalfAll,
  canApproveLeaveManagerStage,
  canApproveLeaveHRStage,
  canApproveLeaveAllStages,
  canApproveRegularizationManagerStage,
  canApproveRegularizationAllStages,
  canManageConfig,
}

